import { useState, useEffect } from "react"
import styled from "styled-components"

const DashboardContainer = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
`

const Header = styled.header`
  background: white;
  padding: 1rem 2rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const Title = styled.h1`
  color: #333;
  margin: 0;
`

const HeaderActions = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`

const LogoutButton = styled.button`
  background: #dc3545;
  color: white;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  
  &:hover {
    background: #c82333;
  }
`

const RefreshButton = styled.button`
  background: #28a745;
  color: white;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  
  &:hover {
    background: #218838;
  }
`

const ExportButton = styled.button`
  background: #17a2b8;
  color: white;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  
  &:hover {
    background: #138496;
  }
`

const Content = styled.main`
  padding: 2rem;
`

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
`

const StatCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: relative;
`

const StatNumber = styled.div`
  font-size: 2rem;
  font-weight: bold;
  color: #007bff;
`

const StatLabel = styled.div`
  color: #666;
  margin-top: 0.5rem;
`

const StatTrend = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  font-size: 0.875rem;
  color: ${(props) => (props.trend === "up" ? "#28a745" : props.trend === "down" ? "#dc3545" : "#6c757d")};
`

const FiltersSection = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
`

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const FilterLabel = styled.label`
  font-weight: 600;
  color: #333;
`

const FilterSelect = styled.select`
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
`

const FilterInput = styled.input`
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
`

const OrdersSection = styled.section`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`

const SectionHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const SectionTitle = styled.h2`
  margin: 0;
  color: #333;
`

const BulkActions = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`

const BulkButton = styled.button`
  background: #6c757d;
  color: white;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.875rem;
  
  &:hover {
    background: #5a6268;
  }
  
  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`

const TableHeader = styled.th`
  background: #f8f9fa;
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: #333;
  border-bottom: 1px solid #eee;
  position: relative;
  cursor: pointer;
  
  &:hover {
    background: #e9ecef;
  }
`

const SortIcon = styled.span`
  position: absolute;
  right: 0.5rem;
  color: #6c757d;
`

const TableRow = styled.tr`
  &:hover {
    background: #f8f9fa;
  }
  
  ${(props) => props.selected && "background: #e3f2fd;"}
`

const TableCell = styled.td`
  padding: 1rem;
  border-bottom: 1px solid #eee;
`

const Checkbox = styled.input`
  margin: 0;
`

const StatusBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 500;
  
  ${(props) => {
    switch (props.status) {
      case "pending":
        return "background: #fff3cd; color: #856404;"
      case "in-progress":
        return "background: #d1ecf1; color: #0c5460;"
      case "completed":
        return "background: #d4edda; color: #155724;"
      case "cancelled":
        return "background: #f8d7da; color: #721c24;"
      default:
        return "background: #e2e3e5; color: #383d41;"
    }
  }}
`

const PriorityBadge = styled.span`
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  margin-left: 0.5rem;
  
  ${(props) => {
    switch (props.priority) {
      case "urgent":
        return "background: #dc3545; color: white;"
      case "high":
        return "background: #fd7e14; color: white;"
      case "medium":
        return "background: #ffc107; color: #212529;"
      case "low":
        return "background: #28a745; color: white;"
      default:
        return "background: #6c757d; color: white;"
    }
  }}
`

const ActionButton = styled.button`
  background: ${(props) => {
    switch (props.variant) {
      case "danger":
        return "#dc3545"
      case "success":
        return "#28a745"
      case "warning":
        return "#ffc107"
      default:
        return "#007bff"
    }
  }};
  color: ${(props) => (props.variant === "warning" ? "#212529" : "white")};
  padding: 0.25rem 0.75rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-right: 0.5rem;
  font-size: 0.875rem;
  
  &:hover {
    opacity: 0.8;
  }
`

const Pagination = styled.div`
  padding: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid #eee;
`

const PageButton = styled.button`
  background: ${(props) => (props.active ? "#007bff" : "white")};
  color: ${(props) => (props.active ? "white" : "#007bff")};
  border: 1px solid #007bff;
  padding: 0.5rem 1rem;
  margin: 0 0.25rem;
  border-radius: 4px;
  cursor: pointer;
  
  &:hover {
    background: #007bff;
    color: white;
  }
  
  &:disabled {
    background: #e9ecef;
    color: #6c757d;
    border-color: #dee2e6;
    cursor: not-allowed;
  }
`

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`

const ModalContent = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 8px;
  max-width: 800px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
`

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
`

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
`

const DetailRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 1rem;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #eee;
`

const DetailLabel = styled.strong`
  color: #333;
`

const NotesSection = styled.div`
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 2px solid #eee;
`

const NoteInput = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  min-height: 100px;
  margin-bottom: 1rem;
`

const AdminDashboard = ({ onLogout, apiEndpoints = {} }) => {
  const [orders, setOrders] = useState([])
  const [filteredOrders, setFilteredOrders] = useState([])
  const [selectedOrders, setSelectedOrders] = useState([])
  const [filters, setFilters] = useState({
    status: "all",
    priority: "all",
    dateFrom: "",
    dateTo: "",
    search: "",
  })
  const [sortConfig, setSortConfig] = useState({ key: "createdAt", direction: "desc" })
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(10)
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [showModal, setShowModal] = useState(false)
  const [orderNotes, setOrderNotes] = useState({})
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    loadOrders()
  }, [])

  useEffect(() => {
    filterAndSortOrders()
  }, [orders, filters, sortConfig])

  const loadOrders = async () => {
    setIsLoading(true)
    try {
      let apiOrders = []

      // Try to fetch from API first
      if (apiEndpoints.getOrders) {
        try {
          const response = await fetch(apiEndpoints.getOrders, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("adminToken") || ""}`,
              "Content-Type": "application/json",
            },
          })

          if (response?.ok) {
            const result = await response.json()
            apiOrders = result?.data?.orders || []
          }
        } catch (apiError) {
          console.error("API fetch failed:", apiError)
        }
      }

      // Always load localStorage orders as backup
      const localOrders = JSON.parse(localStorage.getItem("kitchenOrders") || "[]")

      // Combine API and local orders, removing duplicates by ID
      const combinedOrders = [...apiOrders, ...localOrders]
      const uniqueOrders = combinedOrders.filter(
        (order, index, self) => index === self.findIndex((o) => o.id === order.id),
      )

      setOrders(uniqueOrders)
    } catch (error) {
      console.error("Error loading orders:", error)
      // Fallback to localStorage only
      const localOrders = JSON.parse(localStorage.getItem("kitchenOrders") || "[]")
      setOrders(localOrders)
    } finally {
      setIsLoading(false)
    }
  }

  const filterAndSortOrders = () => {
    let filtered = [...orders]

    // Apply filters
    if (filters.status !== "all") {
      filtered = filtered.filter((order) => (order.status || "") === filters.status)
    }

    if (filters.priority !== "all") {
      filtered = filtered.filter((order) => (order.project?.priority || order.priority || "") === filters.priority)
    }

    if (filters.search) {
      const searchTerm = filters.search.toLowerCase()
      filtered = filtered.filter(
        (order) =>
          (order.customer?.name || order.customerName || "").toLowerCase().includes(searchTerm) ||
          (order.customer?.email || order.email || "").toLowerCase().includes(searchTerm) ||
          (order.id || "").toString().includes(searchTerm),
      )
    }

    if (filters.dateFrom) {
      filtered = filtered.filter((order) => new Date(order.createdAt || "") >= new Date(filters.dateFrom))
    }

    if (filters.dateTo) {
      filtered = filtered.filter((order) => new Date(order.createdAt || "") <= new Date(filters.dateTo))
    }

    // Apply sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aVal = a[sortConfig.key] || ""
        let bVal = b[sortConfig.key] || ""

        // Handle nested properties
        if (sortConfig.key.includes(".")) {
          const keys = sortConfig.key.split(".")
          aVal = keys.reduce((obj, key) => obj?.[key] || "", a)
          bVal = keys.reduce((obj, key) => obj?.[key] || "", b)
        }

        if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1
        if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1
        return 0
      })
    }

    setFilteredOrders(filtered)
    setCurrentPage(1)
  }

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }))
  }

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      // Try API update first
      if (apiEndpoints.updateOrder) {
        try {
          const response = await fetch(`${apiEndpoints.updateOrder}/${orderId}`, {
            method: "PATCH",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("adminToken") || ""}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ status: newStatus }),
          })

          if (!response?.ok) {
            throw new Error("API update failed")
          }
        } catch (apiError) {
          console.error("API update failed:", apiError)
        }
      }

      // Always update localStorage
      const updatedOrders = orders.map((order) => (order.id === orderId ? { ...order, status: newStatus } : order))
      setOrders(updatedOrders)
      localStorage.setItem("kitchenOrders", JSON.stringify(updatedOrders))
    } catch (error) {
      console.error("Error updating order:", error)
    }
  }

  const deleteOrder = async (orderId) => {
    if (!window.confirm("Are you sure you want to delete this order?")) return

    try {
      // Try API delete first
      if (apiEndpoints.deleteOrder) {
        try {
          const response = await fetch(`${apiEndpoints.deleteOrder}/${orderId}`, {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("adminToken") || ""}`,
            },
          })

          if (!response?.ok) {
            throw new Error("API delete failed")
          }
        } catch (apiError) {
          console.error("API delete failed:", apiError)
        }
      }

      // Always update localStorage
      const updatedOrders = orders.filter((order) => order.id !== orderId)
      setOrders(updatedOrders)
      localStorage.setItem("kitchenOrders", JSON.stringify(updatedOrders))
    } catch (error) {
      console.error("Error deleting order:", error)
    }
  }

  const bulkUpdateStatus = async (status) => {
    if (selectedOrders.length === 0) return

    try {
      // Try API bulk update
      if (apiEndpoints.bulkUpdateOrders) {
        try {
          const response = await fetch(apiEndpoints.bulkUpdateOrders, {
            method: "PATCH",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("adminToken") || ""}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              orderIds: selectedOrders,
              status: status,
            }),
          })

          if (!response?.ok) {
            throw new Error("API bulk update failed")
          }
        } catch (apiError) {
          console.error("API bulk update failed:", apiError)
        }
      }

      // Always update localStorage
      const updatedOrders = orders.map((order) => (selectedOrders.includes(order.id) ? { ...order, status } : order))
      setOrders(updatedOrders)
      localStorage.setItem("kitchenOrders", JSON.stringify(updatedOrders))
      setSelectedOrders([])
    } catch (error) {
      console.error("Error bulk updating orders:", error)
    }
  }

  const exportOrders = () => {
    const csvContent = [
      ["Order ID", "Customer", "Email", "Kitchen Type", "Budget", "Status", "Priority", "Date"].join(","),
      ...filteredOrders.map((order) =>
        [
          order.id || "",
          order.customer?.name || order.customerName || "",
          order.customer?.email || order.email || "",
          order.kitchen?.type || order.kitchenType || "",
          order.project?.budget || order.budget || "",
          order.status || "",
          order.project?.priority || order.priority || "",
          new Date(order.createdAt || "").toLocaleDateString(),
        ].join(","),
      ),
    ].join("\n")

    const blob = new Blob([csvContent], { type: "text/csv" })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `kitchen-orders-${new Date().toISOString().split("T")[0]}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
  }

  const addOrderNote = async (orderId, note) => {
    if (!note?.trim()) return

    try {
      // Try API note addition
      if (apiEndpoints.addOrderNote) {
        try {
          const response = await fetch(`${apiEndpoints.addOrderNote}/${orderId}`, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("adminToken") || ""}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ note: note.trim() }),
          })

          if (!response?.ok) {
            throw new Error("API note addition failed")
          }
        } catch (apiError) {
          console.error("API note addition failed:", apiError)
        }
      }

      // Update local notes
      setOrderNotes((prev) => ({
        ...prev,
        [orderId]: [
          ...(prev[orderId] || []),
          {
            id: Date.now(),
            note: note.trim(),
            createdAt: new Date().toISOString(),
            author: "Admin",
          },
        ],
      }))
    } catch (error) {
      console.error("Error adding note:", error)
    }
  }

  const viewOrderDetails = (order) => {
    setSelectedOrder(order)
    setShowModal(true)
  }

  const handleOrderSelection = (orderId, checked) => {
    setSelectedOrders((prev) => (checked ? [...prev, orderId] : prev.filter((id) => id !== orderId)))
  }

  const selectAllOrders = (checked) => {
    setSelectedOrders(checked ? paginatedOrders.map((order) => order.id) : [])
  }

  const getStats = () => {
    const total = orders.length
    const pending = orders.filter((o) => (o.status || "") === "pending").length
    const inProgress = orders.filter((o) => (o.status || "") === "in-progress").length
    const completed = orders.filter((o) => (o.status || "") === "completed").length
    const urgent = orders.filter((o) => (o.project?.priority || o.priority || "") === "urgent").length
    const thisMonth = orders.filter((o) => {
      const orderDate = new Date(o.createdAt || "")
      const now = new Date()
      return orderDate.getMonth() === now.getMonth() && orderDate.getFullYear() === now.getFullYear()
    }).length

    return { total, pending, inProgress, completed, urgent, thisMonth }
  }

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage
  const indexOfFirstItem = indexOfLastItem - itemsPerPage
  const paginatedOrders = filteredOrders.slice(indexOfFirstItem, indexOfLastItem)
  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage)

  const stats = getStats()

  return (
    <DashboardContainer>
      <Header>
        <Title>Kitchen Orders Dashboard</Title>
        <HeaderActions>
          <RefreshButton onClick={loadOrders} disabled={isLoading}>
            {isLoading ? "Loading..." : "Refresh"}
          </RefreshButton>
          <ExportButton onClick={exportOrders}>Export CSV</ExportButton>
          <LogoutButton onClick={onLogout}>Logout</LogoutButton>
        </HeaderActions>
      </Header>

      <Content>
        <StatsGrid>
          <StatCard>
            <StatNumber>{stats.total}</StatNumber>
            <StatLabel>Total Orders</StatLabel>
            <StatTrend trend="up">↗ +12%</StatTrend>
          </StatCard>
          <StatCard>
            <StatNumber>{stats.pending}</StatNumber>
            <StatLabel>Pending Orders</StatLabel>
            <StatTrend trend="down">↘ -5%</StatTrend>
          </StatCard>
          <StatCard>
            <StatNumber>{stats.inProgress}</StatNumber>
            <StatLabel>In Progress</StatLabel>
            <StatTrend trend="up">↗ +8%</StatTrend>
          </StatCard>
          <StatCard>
            <StatNumber>{stats.completed}</StatNumber>
            <StatLabel>Completed</StatLabel>
            <StatTrend trend="up">↗ +15%</StatTrend>
          </StatCard>
          <StatCard>
            <StatNumber>{stats.urgent}</StatNumber>
            <StatLabel>Urgent Orders</StatLabel>
            <StatTrend trend="neutral">→ 0%</StatTrend>
          </StatCard>
          <StatCard>
            <StatNumber>{stats.thisMonth}</StatNumber>
            <StatLabel>This Month</StatLabel>
            <StatTrend trend="up">↗ +20%</StatTrend>
          </StatCard>
        </StatsGrid>

        <FiltersSection>
          <FilterGroup>
            <FilterLabel>Status</FilterLabel>
            <FilterSelect
              value={filters.status}
              onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="in-progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Priority</FilterLabel>
            <FilterSelect
              value={filters.priority}
              onChange={(e) => setFilters((prev) => ({ ...prev, priority: e.target.value }))}
            >
              <option value="all">All Priorities</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </FilterSelect>
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Search</FilterLabel>
            <FilterInput
              type="text"
              placeholder="Search orders..."
              value={filters.search}
              onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
            />
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Date From</FilterLabel>
            <FilterInput
              type="date"
              value={filters.dateFrom}
              onChange={(e) => setFilters((prev) => ({ ...prev, dateFrom: e.target.value }))}
            />
          </FilterGroup>

          <FilterGroup>
            <FilterLabel>Date To</FilterLabel>
            <FilterInput
              type="date"
              value={filters.dateTo}
              onChange={(e) => setFilters((prev) => ({ ...prev, dateTo: e.target.value }))}
            />
          </FilterGroup>
        </FiltersSection>

        <OrdersSection>
          <SectionHeader>
            <SectionTitle>Orders ({filteredOrders.length})</SectionTitle>
            <BulkActions>
              <BulkButton onClick={() => bulkUpdateStatus("in-progress")} disabled={selectedOrders.length === 0}>
                Start Selected
              </BulkButton>
              <BulkButton onClick={() => bulkUpdateStatus("completed")} disabled={selectedOrders.length === 0}>
                Complete Selected
              </BulkButton>
            </BulkActions>
          </SectionHeader>

          <Table>
            <thead>
              <tr>
                <TableHeader>
                  <Checkbox
                    type="checkbox"
                    onChange={(e) => selectAllOrders(e.target.checked)}
                    checked={selectedOrders.length === paginatedOrders.length && paginatedOrders.length > 0}
                  />
                </TableHeader>
                <TableHeader onClick={() => handleSort("id")}>
                  Order ID
                  <SortIcon>{sortConfig.key === "id" ? (sortConfig.direction === "asc" ? "↑" : "↓") : "↕"}</SortIcon>
                </TableHeader>
                <TableHeader onClick={() => handleSort("customer.name")}>
                  Customer
                  <SortIcon>
                    {sortConfig.key === "customer.name" ? (sortConfig.direction === "asc" ? "↑" : "↓") : "↕"}
                  </SortIcon>
                </TableHeader>
                <TableHeader onClick={() => handleSort("kitchen.type")}>Kitchen Type</TableHeader>
                <TableHeader onClick={() => handleSort("project.budget")}>Budget</TableHeader>
                <TableHeader onClick={() => handleSort("status")}>
                  Status
                  <SortIcon>
                    {sortConfig.key === "status" ? (sortConfig.direction === "asc" ? "↑" : "↓") : "↕"}
                  </SortIcon>
                </TableHeader>
                <TableHeader onClick={() => handleSort("project.priority")}>Priority</TableHeader>
                <TableHeader onClick={() => handleSort("createdAt")}>
                  Date
                  <SortIcon>
                    {sortConfig.key === "createdAt" ? (sortConfig.direction === "asc" ? "↑" : "↓") : "↕"}
                  </SortIcon>
                </TableHeader>
                <TableHeader>Actions</TableHeader>
              </tr>
            </thead>
            <tbody>
              {paginatedOrders.map((order) => (
                <TableRow key={order.id} selected={selectedOrders.includes(order.id)}>
                  <TableCell>
                    <Checkbox
                      type="checkbox"
                      checked={selectedOrders.includes(order.id)}
                      onChange={(e) => handleOrderSelection(order.id, e.target.checked)}
                    />
                  </TableCell>
                  <TableCell>#{order.id}</TableCell>
                  <TableCell>{order.customer?.name || order.customerName || "N/A"}</TableCell>
                  <TableCell>{order.kitchen?.type || order.kitchenType || "N/A"}</TableCell>
                  <TableCell>{order.project?.budget || order.budget || "N/A"}</TableCell>
                  <TableCell>
                    <StatusBadge status={order.status || "pending"}>{order.status || "pending"}</StatusBadge>
                  </TableCell>
                  <TableCell>
                    <PriorityBadge priority={order.project?.priority || order.priority || "medium"}>
                      {(order.project?.priority || order.priority || "medium").toUpperCase()}
                    </PriorityBadge>
                  </TableCell>
                  <TableCell>{order.createdAt ? new Date(order.createdAt).toLocaleDateString() : "N/A"}</TableCell>
                  <TableCell>
                    <ActionButton onClick={() => viewOrderDetails(order)}>View</ActionButton>
                    <ActionButton onClick={() => updateOrderStatus(order.id, "in-progress")}>Start</ActionButton>
                    <ActionButton variant="success" onClick={() => updateOrderStatus(order.id, "completed")}>
                      Complete
                    </ActionButton>
                    <ActionButton variant="danger" onClick={() => deleteOrder(order.id)}>
                      Delete
                    </ActionButton>
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          </Table>

          <Pagination>
            <div>
              Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredOrders.length)} of{" "}
              {filteredOrders.length} orders
            </div>
            <div>
              <PageButton onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))} disabled={currentPage === 1}>
                Previous
              </PageButton>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <PageButton key={page} active={page === currentPage} onClick={() => setCurrentPage(page)}>
                  {page}
                </PageButton>
              ))}
              <PageButton
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Next
              </PageButton>
            </div>
          </Pagination>
        </OrdersSection>
      </Content>

      {showModal && selectedOrder && (
        <Modal onClick={() => setShowModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3>Order Details - #{selectedOrder.id}</h3>
              <CloseButton onClick={() => setShowModal(false)}>×</CloseButton>
            </ModalHeader>

            <DetailGrid>
              <div>
                <DetailRow>
                  <DetailLabel>Customer:</DetailLabel>
                  <div>{selectedOrder.customer?.name || selectedOrder.customerName || "N/A"}</div>
                </DetailRow>

                <DetailRow>
                  <DetailLabel>Email:</DetailLabel>
                  <div>{selectedOrder.customer?.email || selectedOrder.email || "N/A"}</div>
                </DetailRow>

                <DetailRow>
                  <DetailLabel>Phone:</DetailLabel>
                  <div>{selectedOrder.customer?.phone || selectedOrder.phone || "N/A"}</div>
                </DetailRow>

                <DetailRow>
                  <DetailLabel>Priority:</DetailLabel>
                  <div>
                    <PriorityBadge priority={selectedOrder.project?.priority || selectedOrder.priority || "medium"}>
                      {(selectedOrder.project?.priority || selectedOrder.priority || "medium").toUpperCase()}
                    </PriorityBadge>
                  </div>
                </DetailRow>
              </div>

              <div>
                <DetailRow>
                  <DetailLabel>Kitchen Type:</DetailLabel>
                  <div>{selectedOrder.kitchen?.type || selectedOrder.kitchenType || "N/A"}</div>
                </DetailRow>

                <DetailRow>
                  <DetailLabel>Budget:</DetailLabel>
                  <div>{selectedOrder.project?.budget || selectedOrder.budget || "N/A"}</div>
                </DetailRow>

                <DetailRow>
                  <DetailLabel>Timeline:</DetailLabel>
                  <div>{selectedOrder.project?.timeline || selectedOrder.projectTimeline || "N/A"}</div>
                </DetailRow>

                <DetailRow>
                  <DetailLabel>Status:</DetailLabel>
                  <div>
                    <StatusBadge status={selectedOrder.status || "pending"}>
                      {selectedOrder.status || "pending"}
                    </StatusBadge>
                  </div>
                </DetailRow>
              </div>
            </DetailGrid>

            <DetailRow>
              <DetailLabel>Address:</DetailLabel>
              <div>{selectedOrder.customer?.address || selectedOrder.address || "N/A"}</div>
            </DetailRow>

            <DetailRow>
              <DetailLabel>Dimensions:</DetailLabel>
              <div>
                {selectedOrder.kitchen?.dimensions?.length || selectedOrder.dimensions?.length || "N/A"}' ×{" "}
                {selectedOrder.kitchen?.dimensions?.width || selectedOrder.dimensions?.width || "N/A"}'
                {(selectedOrder.kitchen?.dimensions?.height || selectedOrder.dimensions?.height) &&
                  ` × ${selectedOrder.kitchen?.dimensions?.height || selectedOrder.dimensions?.height}'`}
              </div>
            </DetailRow>

            <DetailRow>
              <DetailLabel>Cabinet Style:</DetailLabel>
              <div>{selectedOrder.kitchen?.cabinetStyle || selectedOrder.cabinetStyle || "N/A"}</div>
            </DetailRow>

            <DetailRow>
              <DetailLabel>Material:</DetailLabel>
              <div>{selectedOrder.kitchen?.material || selectedOrder.material || "N/A"}</div>
            </DetailRow>

            <DetailRow>
              <DetailLabel>Countertop:</DetailLabel>
              <div>{selectedOrder.kitchen?.countertopMaterial || selectedOrder.countertopMaterial || "N/A"}</div>
            </DetailRow>

            <DetailRow>
              <DetailLabel>Appliances:</DetailLabel>
              <div>{(selectedOrder.kitchen?.appliances || selectedOrder.appliances || []).join(", ") || "None"}</div>
            </DetailRow>

            {(selectedOrder.project?.installationDate || selectedOrder.installationDate) && (
              <DetailRow>
                <DetailLabel>Installation Date:</DetailLabel>
                <div>
                  {new Date(
                    selectedOrder.project?.installationDate || selectedOrder.installationDate,
                  ).toLocaleDateString()}
                </div>
              </DetailRow>
            )}

            {(selectedOrder.design?.colorScheme || selectedOrder.designPreferences?.colorScheme) && (
              <DetailRow>
                <DetailLabel>Color Scheme:</DetailLabel>
                <div>{selectedOrder.design?.colorScheme || selectedOrder.designPreferences?.colorScheme}</div>
              </DetailRow>
            )}

            {(selectedOrder.metadata?.referralSource || selectedOrder.referralSource) && (
              <DetailRow>
                <DetailLabel>Referral Source:</DetailLabel>
                <div>{selectedOrder.metadata?.referralSource || selectedOrder.referralSource}</div>
              </DetailRow>
            )}

            {(selectedOrder.project?.specialRequirements || selectedOrder.specialRequirements) && (
              <DetailRow>
                <DetailLabel>Special Requirements:</DetailLabel>
                <div>{selectedOrder.project?.specialRequirements || selectedOrder.specialRequirements}</div>
              </DetailRow>
            )}

            <NotesSection>
              <h4>Order Notes</h4>
              {(orderNotes[selectedOrder.id] || []).map((note) => (
                <div
                  key={note.id}
                  style={{
                    background: "#f8f9fa",
                    padding: "1rem",
                    marginBottom: "0.5rem",
                    borderRadius: "4px",
                    borderLeft: "4px solid #007bff",
                  }}
                >
                  <div style={{ fontSize: "0.875rem", color: "#666", marginBottom: "0.5rem" }}>
                    {note.author} - {new Date(note.createdAt).toLocaleString()}
                  </div>
                  <div>{note.note}</div>
                </div>
              ))}

              <NoteInput
                placeholder="Add a note about this order..."
                onKeyPress={(e) => {
                  if (e.key === "Enter" && e.ctrlKey) {
                    addOrderNote(selectedOrder.id, e.target.value)
                    e.target.value = ""
                  }
                }}
              />
              <div style={{ fontSize: "0.875rem", color: "#666", marginTop: "0.5rem" }}>
                Press Ctrl+Enter to add note
              </div>
            </NotesSection>
          </ModalContent>
        </Modal>
      )}
    </DashboardContainer>
  )
}

export default AdminDashboard
